/*
 * fn: parseRoute
 *
 * Parses an AppRouter request to a PageType, using a provided section to build a route object
 *
 */
import { notFound, permanentRedirect, redirect } from 'next/navigation'

import { type Dict } from '@/defs/misc'
import {
  getRouteConfig,
  getRouteParamConfig,
  isRouteParamType,
  type RouteParamsType,
  type RouteSectionType,
  type RouteType,
} from '@/defs/route'
import { isTagRenameType, TAG_RENAMES } from '@/defs/tag'

import { buildRoute } from './buildRoute'
import { getRouteTemplateSegments } from './getRouteTemplateSegments'
import { parseRouteParam } from './parseRouteParam'

type Props = {
  catchall?: string[]
  query?: Dict<string | string[]>
  redirectOnInvalid?: string
  section: RouteSectionType
}

export const parseRoute = ({ catchall, query, redirectOnInvalid, section }: Props) => {
  const handleInvalid: () => never = () => (redirectOnInvalid ? redirect(redirectOnInvalid) : notFound())

  const params: RouteParamsType = {}
  const setParam = <K extends keyof RouteParamsType>(key: K, value: RouteParamsType[K]) => {
    params[key] = value
  }

  const { queryParams, tabs, template } = getRouteConfig(section)
  const items = [...(catchall || [])]
  const lastItem = items.at(-1)

  /* Only a tab with its own slug takes a path segment. */
  const tab = tabs?.find((t) => !!t.route.tab && t.route.tab === lastItem)

  if (tab) {
    items.pop()
  } else if (tabs?.some((t) => t.route.tab !== undefined && t.value === lastItem)) {
    handleInvalid()
  }

  let item = items.shift()

  for (const { type, value } of getRouteTemplateSegments(template)) {
    if (type === 'literal') continue
    if (!isRouteParamType(value)) handleInvalid()

    const parsed = parseRouteParam(value, item)

    if (parsed !== undefined) {
      setParam(value, parsed)
      item = items.shift()
    } else if (type === 'required') {
      handleInvalid()
    }
  }

  if (item !== undefined || items.length > 0) handleInvalid()

  for (const param of queryParams || []) {
    const { check, isStrict, query: queryKey } = getRouteParamConfig(param)
    if (!queryKey) continue

    const queryItem = query?.[queryKey]
    if (typeof queryItem !== 'string') continue

    if (isStrict && !check(queryItem)) handleInvalid()

    const parsed = parseRouteParam(param, queryItem)
    if (parsed !== undefined) setParam(param, parsed)
  }

  const route: RouteType = { ...params, section, tab: tab?.route.tab }

  if (isTagRenameType(route.tagSlug)) {
    const renamed = buildRoute({ ...route, tagSlug: TAG_RENAMES[route.tagSlug] })
    if (renamed) permanentRedirect(renamed)
  }

  return route
}
